import * as React from 'react';

import { Tab, Tabs } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

import { getAccessToken, type User } from '@/auth';
import Log from '@/Logger';
import { isDeepEqual } from '@/util';

import KintoneDialog, { type kintoneApp, TITLE } from '../util/kintone/kintoneAppsDialog';
import { linkServiceTokenExpiredDate } from './api';
import { invoke_pdfSender } from './common';
import DeviceList, { type DeviceListHandle } from './DevieList';
import ResultListTable, { ItemStatus, type SendResult } from './ResultListTable';
import SelectedItemListTable from './SelectedItemListTable';
import { generateKintonePdfSenderPayload, isExpired, isValidSize, isValidType, sendDocument } from './SendDocument.hooks';

type Props = {
    user: User;
};

const SendDocument = (props: Props) => {
    const [fileList, setFileList] = React.useState<File[]>([]);
    const [sendResultList, setSendResultList] = React.useState<SendResult[]>([]);
    const [isPending, setIsPending] = React.useState<boolean>(true);
    const [isLoading, setIsloading] = React.useState<boolean>(false);
    const [notificationList, setNotificationList] = React.useState<string[]>([]);
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarText, setSnackBarText] = React.useState('');
    const [snackBarType, setSnackBarType] = React.useState<any>(undefined);
    const [isKintoneLoggedIn, setIsKintoneLoggedIn] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [kintoneApps, setKintoneApps] = React.useState<kintoneApp[]>([]);

    const deviceListHandle = React.useRef<DeviceListHandle>(null);
    const { selectedDevices = [] } = deviceListHandle.current ?? {};

    //一度に送信できるPDFファイル数を定義
    const MAX_FILE_NUM = 3;
    const MAX_KINTONE_APP_NUM = 3;

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackBarOpen(false);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };
    const handleKintoneAppsArraySelect = (kintoneAppsArray: kintoneApp[]) => {
        setKintoneApps(kintoneAppsArray); // 選択されたアイテムを保存
    };

    //初回のみ実行(useEffectの第二引数に空の配列を指定しているため)
    React.useEffect(() => {
        const logger = Log.getLogger();
        //kintoneのユーザー登録がDBにあるかチェック
        logger.ui.info('SendDocument', 'useEffect', 'Start to get kintone token expiredDate');
        linkServiceTokenExpiredDate('kintone').then((tokenExpiredDate) => {
            if (!isExpired(tokenExpiredDate.refreshTokenExpiredDate)) {
                setIsKintoneLoggedIn(true)
            }
        })
    }, []);

    //ファイルとkintoneアプリを排他にするための仕組み(どちらか一方が追加されるともう一方を空にする)
    React.useEffect(() => {
        if (fileList.length > 0) {
            setKintoneApps([]);
        }
    }, [fileList]);
    React.useEffect(() => {
        if (kintoneApps.length > 0) {
            setFileList([]);
        }
    }, [kintoneApps]);

    // 選択デバイス・ファイルを変更した時の処理
    React.useEffect(() => {
        const sendPattern: SendResult[] = selectedDevices.map((device) => {

            if (fileList.length > 0) {
                const sendFileList = fileList.map((file) => Object.fromEntries([['sendItem', file], ['result', ItemStatus.PENDING],]));
                return Object.fromEntries([['device', device], ['sendItemList', [...sendFileList]],]);
            }

            if (kintoneApps.length > 0) {
                const sendKintoneAppsList = kintoneApps.map((selectedApp) => Object.fromEntries([['sendItem', selectedApp], ['result', ItemStatus.PENDING],]));
                return Object.fromEntries([['device', device], ['sendItemList', [...sendKintoneAppsList]],]);
            }

            return Object.fromEntries([['device', device], ['sendItemList', []],]);
        });

        // 状態を変更する前に、前回の値と比較
        setSendResultList((prev) => {
            if (!isDeepEqual(prev, sendPattern)) {
                setIsPending(true);
                return sendPattern;
            }
            return prev; // 前回と同じ場合は更新しない
        });
    }, [fileList, kintoneApps, selectedDevices]);


    // クライアントでファイルを選択したときの処理
    const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        const logger = Log.getLogger();
        logger.ui.info('SendDocument', 'onChangeFile');
        const files: FileList | null = event.target.files;
        if (!files) return;

        // ファイルを選択するたびにファイルリストとエラーリストをリセット
        const fileListToSet: File[] = [];
        const notificationListToSet: string[] = [];

        // 入力されたファイルが既定の個数以下かどうかチェック
        if (files.length > MAX_FILE_NUM) {
            logger.ui.info('SendDocument', 'onChangeFile', `More than ${MAX_FILE_NUM} Files is selected`);
            notificationListToSet.push(`ファイルは一度に${MAX_FILE_NUM}個までしか送信できません`);
        } else {
            Array.from(files).forEach((file) => {
                if (!isValidType(file)) {
                    logger.ui.info('SendDocument', 'onChangeFile', 'Selected file is not PDF');
                    notificationListToSet.push(`${file.name} はPDF形式でないため送信できません`);
                    return;
                }
                if (!isValidSize(file)) {
                    logger.ui.info('SendDocument', 'onChangeFile', 'Larger than 10Mb File is selected');
                    notificationListToSet.push(`${file.name} は10MBを超えるため送信できません`);
                    return;
                }
            });

            // すべてのファイルが条件を満たす場合
            if (notificationListToSet.length === 0) {
                Array.from(files).forEach((file) => {
                    fileListToSet.push(file);
                });
                logger.ui.info('SendDocument', 'onChangeFile', 'Files are selected');
            }
        }

        setFileList(fileListToSet);
        setNotificationList(notificationListToSet);
    };



    // デバイスを指定して1ファイルを送信する処理
    const sendFileToDevice = async (deviceId: string, file: File): Promise<number> => {
        return new Promise((resolve, reject) => {
            const logger = Log.getLogger();
            sendDocument(deviceId, file, setIsloading)
                .then((res: Response) => {
                    if (res.status === 200) {
                        logger.ui.info('SendDocument', 'onClickSend', 'Scceed to send document');
                        setSnackBarType('success');
                        setSnackBarText(file.name + 'の送信要求に成功しました');
                        setSnackBarOpen(true);
                    } else {
                        logger.ui.error('SendDocument', 'onClickSend', 'Fail to send document:' + res.status);
                        setSnackBarType('error');
                        setSnackBarText(file.name + 'の送信要求に失敗しました');
                        setSnackBarOpen(true);
                    }
                    resolve(res.status);
                })
                .catch((err) => {
                    logger.ui.error('SendDocument', 'onClickSend', 'Fail to send document:' + err.deviceId);
                    setSnackBarType('error');
                    setSnackBarText(file.name + 'の送信要求に失敗しました');
                    setSnackBarOpen(true);
                    reject(0);
                });
        });
    };

    // 送信ボタンを押下した時の処理(PDF, kintone共通)
    const onClickSend = async () => {
        const logger = Log.getLogger();
        logger.ui.info('SendDocument', 'onClickSend', 'SendButton is pushed');

        setIsPending(false);
        setNotificationList([]);

        // デバイスとファイルの組み合わせで送信結果を格納するオブジェクト
        const tempSendResultList: SendResult[] = [...sendResultList];

        // すべての状態を待機中に
        tempSendResultList.forEach((item) => item.sendItemList.forEach((item) => (item.result = ItemStatus.AWAITING)));

        // setして表示をくるくるマークに
        setSendResultList(tempSendResultList);

        //送信するものがファイルのとき
        if (fileList.length !== 0) {
            for (const sendResult of tempSendResultList) {
                for (const file of sendResult.sendItemList) {
                    if (file.sendItem instanceof File) {
                        await sendFileToDevice(sendResult.device.deviceId, file.sendItem)
                            .then((status) => {
                                file.result = status === 200 ? ItemStatus.SUCCESS : ItemStatus.ERROR;
                            })
                            .catch(() => {
                                file.result = ItemStatus.ERROR;
                            });
                        setSendResultList(tempSendResultList);
                    }
                }
            }

            logger.ui.info('SendDocument', 'onClickSend', 'Finish to send PDFFile');
        }

        //送信するものがkintoneアプリの時
        if (kintoneApps.length !== 0) {
            setIsloading(true)
            for (const sendResult of tempSendResultList) {
                for (const selectedApp of sendResult.sendItemList) {

                    try {
                        //lambdaの引数に必要なCognitoアクセストークンを取得
                        const accessToken = await getAccessToken()
                        const accessTokenStr = accessToken.toString()

                        //指定されたアプリIDのレイアウト、フォーム情報を取得してマージし、Lambdaのペイロードに載せて呼び出す
                        const mergedJson = await generateKintonePdfSenderPayload((selectedApp.sendItem as kintoneApp).id, selectedApp.sendItem.name, sendResult.device.deviceId, accessTokenStr);
                        const res = await invoke_pdfSender(mergedJson);

                        selectedApp.result = res.status === 'success' ? ItemStatus.SUCCESS : ItemStatus.ERROR;

                    } catch (error) {
                        selectedApp.result = ItemStatus.ERROR;
                    }

                    // 更新された状態でリストを再設定
                    setSendResultList([...tempSendResultList]);
                }
            }
            setIsloading(false)

            logger.ui.info('SendDocument', 'onClickSendToKintone', 'Finish to send Kintone');
        }

    };

    const [tabIndex, setTabIndex] = React.useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    return (
        <Grid container sx={{ padding: 2, paddingBottom: 4, maxHeight: 'calc(100vh - 63px)', overflowY: 'auto' }}>
            <Grid size={12} display='flex' justifyContent='center'>
                <Card sx={{ minWidth: 500 }}>
                    <CardContent>
                        <Typography variant='h4' component='div' sx={{ flexGrow: 1 }}>
                            デバイスにPDFを送信する
                        </Typography>

                        <Box sx={{ marginY: 2 }}>
                            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                                デバイスを選択する
                            </Typography>
                            <Typography sx={{ mb: 1.5, fontSize: '0.8rem' }} color='text.secondary'>
                                一度も起動していないデバイスは選択肢に表示されないのでご注意ください
                            </Typography>
                            <DeviceList ref={deviceListHandle} />
                        </Box>

                        <Divider />

                        {/* タブの切り替え */}
                        <Tabs value={tabIndex} onChange={handleTabChange}>
                            <Tab label='PDFファイル' />
                            {isKintoneLoggedIn && <Tab label='kintoneアプリ' sx={{ textTransform: 'none' }} />}
                        </Tabs>

                        {/* タブで送信先を切り替える */}
                        {tabIndex === 0 && (
                            <Box sx={{ marginY: 2 }}>
                                <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                                    PDFファイルを選択する
                                </Typography>
                                <Typography sx={{ mb: 1.5, fontSize: '0.8rem' }} color='text.secondary'>
                                    10MB以下のファイルを一度に{MAX_FILE_NUM}個まで送信できます
                                </Typography>
                                <Tooltip title={isLoading ? 'しばらくお待ちください' : ''}>
                                    <span>
                                        <Button variant='outlined' component='label' sx={{ height: '56px' }} disabled={isLoading}>
                                            ファイル選択
                                            <input name='file' type='file' accept='application/pdf' onChange={onChangeFile} value='' hidden multiple />
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}

                        {tabIndex === 1 && (
                            <Box sx={{ marginY: 2 }}>
                                <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
                                    kintoneアプリを選択する
                                </Typography>
                                <Typography sx={{ mb: 1.5, fontSize: '0.8rem' }} color='text.secondary'>
                                    kintoneアプリをeWhiteboardに送信します <br />
                                    一度に{MAX_KINTONE_APP_NUM}個まで送信できます
                                </Typography>
                                <Tooltip title={isLoading ? 'しばらくお待ちください' : 'お客様のkintone環境からeWhiteboardに送るアプリを選択してください'}>
                                    <span>
                                        <Button variant='outlined' component='label' sx={{ height: '56px', textTransform: 'none' }} onClick={() => setDialogOpen(true)} disabled={isLoading}>
                                            kintoneアプリ選択
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                        <Divider />

                        <KintoneDialog
                            open={dialogOpen}
                            onClose={handleDialogClose}
                            onSelect={handleKintoneAppsArraySelect}
                            maxSelectableNum={MAX_KINTONE_APP_NUM}
                            dialogDescription='eWhiteboard4200に送信するkintoneアプリを選択してください。一部の項目は反映されません。'
                            dialogTitle={TITLE}
                        />

                        {notificationList.map((item, index) => (
                            <Box sx={{ marginY: 1 }} key={index}>
                                <Alert severity='error'>{item}</Alert>
                            </Box>
                        ))}
                    </CardContent>

                    <CardActions>
                        <Button
                            data-testid='send-button'
                            variant='contained'
                            onClick={onClickSend}
                            disabled={isLoading || !(
                                (fileList.length > 0 && selectedDevices.length > 0 && fileList.length < MAX_FILE_NUM + 1) ||
                                (kintoneApps.length > 0 && selectedDevices.length > 0)//kintoneアプリの数チェックはココでは行わない
                            )}
                        >
                            {isLoading ? <CircularProgress size={16} /> : <span>デバイスにPDFを送信する</span>}
                        </Button>
                    </CardActions>
                    <CardContent>
                        <Divider />
                        <Box marginTop={2}>{isPending ? <SelectedItemListTable
                            file={{ items: fileList, setItems: setFileList }}
                            kintoneApp={{ items: kintoneApps, setItems: setKintoneApps }}
                        /> : <ResultListTable sendResultList={sendResultList} />}</Box>


                    </CardContent>
                </Card>
            </Grid>
            <Snackbar data-testid='snackbar' open={snackBarOpen} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleClose} severity={snackBarType} sx={{ width: '100%' }}>
                    {snackBarText}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default SendDocument;
